import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TestDisplay from '../common/TestDisplay';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';

const defaultFilters = {
  examId: '',
  subjectId: '',
  topicId: '',
  type: 'all',
};

const TestList = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState(defaultFilters);
  const [pendingFilters, setPendingFilters] = useState(defaultFilters);
  const [filterOptions, setFilterOptions] = useState({
    exams: [],
    subjects: [],
    topics: [],
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({ 
    totalPages: 1, 
    total: 0, 
    page: 1, 
    limit: 9, 
    hasMore: false 
  });
  const [view, setView] = useState('grid');
  const navigate = useNavigate();
  const limit = 9;

  const fetchTests = async (pageNum = 1, append = false, customFilters = filters, customSearch = search) => {
    setLoading(true);
    try {
      const params = { 
        ...customFilters, 
        search: customSearch || undefined, 
        page: pageNum, 
        limit,
        type: customFilters.type || 'all' // Ensure type is always set
      };
      
      const response = await axios.get('/api/tests', { 
        params, 
        withCredentials: true 
      });
      
      const { tests = [], filters = {}, pagination = {} } = response.data;
      
      // Update tests state
      setTests(prev => append ? [...prev, ...tests] : tests);
      
      // Update pagination state
      const updatedPagination = {
        totalPages: pagination.totalPages || 1,
        total: pagination.total || 0,
        page: pagination.page || 1,
        limit: pagination.limit || limit,
        hasMore: pagination.hasMore || false
      };
      
      setPagination(updatedPagination);
      setHasMore(updatedPagination.hasMore);
      
      // Update filter options
      setFilterOptions({
        exams: filters.exams || [],
        subjects: filters.subjects || [],
        topics: filters.topics || []
      });
      
      setError(null);
    } catch (err) {
      console.error('Error fetching tests:', err);
      setError('Failed to fetch tests. Please try again.');
      setTests([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        total: 0,
        page: 1,
        hasMore: false
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTests(page, false);
    // eslint-disable-next-line
  }, [page, filters, search]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setPendingFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFilters(pendingFilters);
    setSearch(searchInput);
    setPage(1);
  };

  const handleReset = () => {
    setPendingFilters(defaultFilters);
    setFilters(defaultFilters);
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleTestClick = (test) => {
    navigate(`/take-test/${test.id}`);
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (  
    <Container maxWidth={false}>
      <Box width="100%" mx="auto" py={{ xs: 2, sm: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 2, sm: 2.5 }}>
          <Typography variant="h5" fontWeight={700}>Available Tests</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, sm: 2 } }}>
            <IconButton color={view === 'grid' ? 'primary' : 'default'} onClick={() => setView('grid')}>
              <GridViewIcon />
            </IconButton>
            <IconButton color={view === 'list' ? 'primary' : 'default'} onClick={() => setView('list')}>
              <ListIcon />
            </IconButton>
          </Box>
        </Box>
         <form onSubmit={handleFormSubmit}>
        
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1.5, sm: 2 }} 
            mb={{ xs: 2, sm: 3 }} 
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
            justifyContent="space-between"
          >
            <Box sx={{ display: 'flex', flex: 1, gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Search"
                variant="outlined"
                value={searchInput}
                onChange={handleSearchInputChange}
                size="small"
                sx={{ 
                  minWidth: { xs: '100%', sm: 180 },
                  flex: { xs: '1 1 100%', sm: '0 1 auto' }
                }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                <InputLabel>Exam</InputLabel>
                <Select
                  name="examId"
                  value={pendingFilters.examId}
                  label="Exam"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All Exams</MenuItem>
                  {filterOptions.exams.map((exam) => (
                    <MenuItem key={exam.id} value={exam.id}>{exam.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                <InputLabel>Subject</InputLabel>
                <Select
                  name="subjectId"
                  value={pendingFilters.subjectId}
                  label="Subject"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All Subjects</MenuItem>
                  {filterOptions.subjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>{subject.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                <InputLabel>Topic</InputLabel>
                <Select
                  name="topicId"
                  value={pendingFilters.topicId}
                  label="Topic"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All Topics</MenuItem>
                  {filterOptions.topics.map((topic) => (
                    <MenuItem key={topic.id} value={topic.id}>{topic.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={pendingFilters.type}
                  label="Type"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="full_syllabus">Full Syllabus</MenuItem>
                  <MenuItem value="subject">Subject Wise</MenuItem>
                  <MenuItem value="topic">Topic Wise</MenuItem>
                </Select>
              </FormControl>
              <Button type="submit" variant="contained">Search</Button>
              <Button type="button" variant="outlined" onClick={handleReset}>Reset</Button>
            </Box>
            
          </Stack>
        </form>
        <TestDisplay tests={tests} onTestClick={handleTestClick} view={view} setView={setView} />
        {pagination.total > 0 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={(e, value) => {
                setPage(value);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              color="primary"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default TestList;
